/**
 * @fileoverview CategoryAccordion — collapsible row that reveals a
 * RowLevelExplorer when expanded.
 * @module import-editor/dryRunSummary
 *
 * Responsibilities:
 * - Render the accordion header (label + count pill + severity badge).
 * - Toggle expanded state and lazy-render the children only when open so
 *   we don't fetch /rows for every category up front.
 *
 * Dependencies:
 * - React
 *
 * Architecture:
 * - Presentation-only. All fetch logic lives inside the child (RowLevelExplorer).
 *
 * Notes:
 * - Clicking a disabled accordion (count === 0) is a no-op.
 * - The accordion remembers its open/closed state locally; the parent
 *   doesn't need to persist it.
 */

/**
 * @component CategoryAccordion
 *
 * Purpose:
 * - A single drill-in row under a category group header.
 *
 * Props:
 * - descriptor: CategoryDescriptor built by deriveCategories
 * - children:   the expanded content (typically a RowLevelExplorer)
 * - defaultOpen: start expanded? defaults to false
 *
 * Behavior:
 * - Children mount on first expansion and stay mounted after (so subsequent
 *   re-opens are instant — the 10-min server cache does the rest).
 *
 * Data Source:
 * - none (container)
 *
 * Constraints:
 * - Disabled when descriptor.count === 0. Cannot be opened in that state.
 */

import { useState, type ReactNode } from 'react';
import type { CategoryDescriptor, CategorySeverity } from './types';

const SEVERITY_PILL: Record<CategorySeverity, string> = {
  ok:     'bg-emerald-100 text-emerald-800 border-emerald-200',
  warn:   'bg-amber-100 text-amber-800 border-amber-200',
  danger: 'bg-red-100 text-red-800 border-red-200',
  info:   'bg-blue-100 text-blue-800 border-blue-200',
};

export interface CategoryAccordionProps {
  descriptor: CategoryDescriptor;
  children?: ReactNode;
  defaultOpen?: boolean;
}

export function CategoryAccordion(props: CategoryAccordionProps): JSX.Element {
  const { descriptor, children, defaultOpen = false } = props;
  const [open, setOpen] = useState(defaultOpen && !descriptor.disabled);
  const [hasOpened, setHasOpened] = useState(open);

  const handleToggle = (): void => {
    if (descriptor.disabled) return;
    setOpen((v) => {
      const next = !v;
      if (next) setHasOpened(true);
      return next;
    });
  };

  return (
    <div className="border border-gray-200 rounded-md overflow-hidden bg-white">
      <button
        type="button"
        onClick={handleToggle}
        disabled={descriptor.disabled}
        aria-expanded={open}
        aria-controls={`accordion-panel-${descriptor.key}`}
        className={`w-full flex items-center gap-3 px-3 py-2 text-left text-sm ${
          descriptor.disabled
            ? 'cursor-default text-gray-400'
            : 'hover:bg-gray-50 text-gray-800'
        }`}
      >
        <span aria-hidden="true" className="text-gray-400 text-xs w-3">
          {descriptor.disabled ? '·' : open ? '▾' : '▸'}
        </span>
        <span className="font-medium flex-1">{descriptor.label}</span>
        <span
          className={`inline-flex items-center rounded-full border px-2 py-0.5 text-[11px] font-semibold tabular-nums ${
            descriptor.disabled
              ? 'bg-gray-50 text-gray-400 border-gray-200'
              : SEVERITY_PILL[descriptor.severity]
          }`}
        >
          {descriptor.count.toLocaleString()}
        </span>
      </button>

      {descriptor.description && open && (
        <div className="px-3 pt-1 pb-2 text-[11px] text-gray-500 border-t border-gray-100">
          {descriptor.description}
        </div>
      )}

      {hasOpened && (
        <div
          id={`accordion-panel-${descriptor.key}`}
          role="region"
          aria-label={descriptor.label}
          className={`px-3 pb-3 ${open ? '' : 'hidden'}`}
        >
          {children}
        </div>
      )}
    </div>
  );
}

export default CategoryAccordion;
